
import { useEffect, useRef, useState } from "react";
import { Sparkles, Gauge, Clock, Fuel, Plus, ArrowUp, Mic, ShieldCheck, MousePointer2 } from "lucide-react";
/* Scripted, looping "rentAI agent at work" demo shown on the wide rentAI card.
   The component fills the Bookings-portal backdrop, so it runs a short story:
     0. Portal: a cursor travels across the portal to the rentAI icon in the
        bottom-left sidebar, hover-highlights it and clicks — opening the chat.
     1. Intro: a centered greeting ("Hey Rental Brand, how can I help?") above a
        single wide chat input — no full chat thread yet.
     2. The host types "Are there any violations on this rental?" into the input.
     3. The panel switches to a compact conversation: the prompt, the Operations
        agent "thinking", then the violations laid out in a wide 3-across row
        (mileage, late return, low fuel) each with start level, current reading
        and overage amount, plus the total.
     4. rentAI proposes creating an Incident and collecting funds; the host says Yes.
     5. The rental's status badge flips to "Funds collected".
   All motion is JS-timeline driven (setTimeout) and cleaned up on unmount. Under
   prefers-reduced-motion it renders the finished resting state with no animation.

   The chat panel is a smaller centered surface that floats on top of the portal;
   the cursor overlay spans the full portal so it can reach the sidebar icon whose
   real position in the backdrop image is ~3% from the left / ~75% from the top. */
const PROMPT = "Are there any violations on this rental?";
const VIOLATIONS = [
    { icon: Gauge, title: "Mileage overage", start: "Included 200 mi", current: "Driven 247 mi", overage: "+47 mi", amount: "$23.50" },
    { icon: Clock, title: "Late return", start: "Due 10:00 AM", current: "Returned 12:15 PM", overage: "+2h 15m", amount: "$40.00" },
    { icon: Fuel, title: "Low fuel", start: "Picked up Full", current: "Returned 1/4 tank", overage: "-3/4 tank", amount: "$28.00" },
];
const TOTAL = "$91.50";
export function RentAiAgentPreview() {
    const [scene, setScene] = useState("portal");
    const [cursorTarget, setCursorTarget] = useState("start");
    const [clicked, setClicked] = useState(false);
    const [typedPrompt, setTypedPrompt] = useState("");
    const [promptSent, setPromptSent] = useState(false);
    const [thinking, setThinking] = useState(false);
    const [violationsShown, setViolationsShown] = useState(0);
    const [askShown, setAskShown] = useState(false);
    const [typedReply, setTypedReply] = useState("");
    const [replySent, setReplySent] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [fundsCollected, setFundsCollected] = useState(false);
    const scrollRef = useRef(null);
    useEffect(() => {
        const prefersReduced = typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (prefersReduced) {
            setScene("chat");
            setCursorTarget("icon");
            setTypedPrompt(PROMPT);
            setPromptSent(true);
            setViolationsShown(VIOLATIONS.length);
            setAskShown(true);
            setTypedReply("Yes");
            setReplySent(true);
            setFundsCollected(true);
            return;
        }
        const timers = [];
        const at = (ms, fn) => timers.push(setTimeout(fn, ms));
        const typeString = (startMs, str, setter, charMs = 45) => {
            for (let i = 1; i <= str.length; i++)
                at(startMs + i * charMs, () => setter(str.slice(0, i)));
            return startMs + str.length * charMs;
        };
        const run = () => {
            setScene("portal");
            setCursorTarget("start");
            setClicked(false);
            setTypedPrompt("");
            setPromptSent(false);
            setThinking(false);
            setViolationsShown(0);
            setAskShown(false);
            setTypedReply("");
            setReplySent(false);
            setProcessing(false);
            setFundsCollected(false);
            // Portal: cursor travels to the rentAI sidebar icon, hovers, then clicks.
            at(600, () => setCursorTarget("icon"));
            at(1900, () => setClicked(true));
            at(2350, () => setScene("intro"));
            const promptEnd = typeString(2900, PROMPT, setTypedPrompt);
            at(promptEnd + 400, () => {
                setScene("chat");
                setPromptSent(true);
                setThinking(true);
            });
            at(promptEnd + 1900, () => setThinking(false));
            at(promptEnd + 2000, () => setViolationsShown(1));
            at(promptEnd + 2400, () => setViolationsShown(2));
            at(promptEnd + 2800, () => setViolationsShown(3));
            at(promptEnd + 4200, () => setAskShown(true));
            const replyStart = promptEnd + 5700;
            const replyEnd = typeString(replyStart, "Yes", setTypedReply, 150);
            at(replyEnd + 300, () => {
                setReplySent(true);
                setProcessing(true);
            });
            at(replyEnd + 1700, () => {
                setProcessing(false);
                setFundsCollected(true);
            });
            at(replyEnd + 5200, run);
        };
        run();
        return () => timers.forEach(clearTimeout);
    }, []);
    useEffect(() => {
        const el = scrollRef.current;
        if (el)
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }, [promptSent, thinking, violationsShown, askShown, typedReply, replySent, processing, fundsCollected]);
    return (<div className="absolute inset-0" role="img" aria-label="Animation of a cursor opening the rentAI agent, which finds violations on a rental and collects funds after host approval">
      {/* ---------------- Portal scene: cursor moves to the rentAI icon ---------------- */}
      <div className={`pointer-events-none absolute inset-0 transition-opacity duration-500 ${scene === "portal" ? "opacity-100" : "opacity-0"}`}>
        {/* Hover / click highlight aligned to the rentAI icon in the portal backdrop. */}
        <span className="absolute" style={{ left: "3%", top: "75.5%", transform: "translate(-50%, -50%)" }} aria-hidden="true">
          <span className={`block h-6 w-6 rounded-lg transition-all duration-300 ${clicked
            ? "scale-110 bg-[#3D8BFF]/30 ring-2 ring-[#3D8BFF]/70"
            : cursorTarget === "icon"
                ? "bg-white/15 ring-1 ring-white/40"
                : "bg-transparent"}`}/>
          {clicked && (<span className="absolute inset-0 animate-ping rounded-xl bg-[#3D8BFF]/50" aria-hidden="true"/>)}
        </span>

        {/* Cursor pointer. The MousePointer2 hotspot (tip) sits at the top-left of
            the glyph, so nudge the box up-left of the icon center to land the tip
            on the center of the highlighted logo (3% / 75.5%). */}
        <div className="absolute transition-all duration-[1300ms] ease-in-out" style={{
            left: cursorTarget === "icon" ? "3%" : "60%",
            top: cursorTarget === "icon" ? "75.5%" : "42%",
            transform: cursorTarget === "icon" ? "translate(-2px, -2px)" : "none",
        }} aria-hidden="true">
          <MousePointer2 className="h-5 w-5 fill-white text-black drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]"/>
        </div>
      </div>

      {/* Blur wash over the portal backdrop once the chat opens, so the floating
            chat panel reads clearly. backdrop-blur blurs the portal image beneath it. */}
      <div aria-hidden="true" className={`pointer-events-none absolute inset-0 rounded-2xl backdrop-blur-sm transition-opacity duration-500 ${scene === "portal" ? "opacity-0" : "opacity-100"}`}/>

      {/* ---------------- Chat panel (floats centered on top of the portal) ---------------- */}
      <div className={`absolute inset-0 flex items-center justify-center p-3 transition-all duration-500 sm:p-5 lg:p-8 ${scene === "portal" ? "pointer-events-none scale-95 opacity-0" : "scale-100 opacity-100"}`}>
        <div className="w-[82%] max-w-lg">
          <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-black/10 bg-white text-[#0b0b12] shadow-[0_18px_40px_rgba(0,0,0,0.55)]">
            {/* ---- Intro scene: greeting + single wide input ---- */}
            <div className={`absolute inset-0 flex flex-col items-center justify-center px-[6%] transition-opacity duration-500 ${scene === "intro" ? "opacity-100" : "pointer-events-none opacity-0"}`}>
              <p className="rentai-gradient-text text-center text-lg font-semibold sm:text-xl lg:text-2xl">
                Hey Rental Brand, how can I help?
              </p>
              <div className="mt-5 w-full max-w-xl">
                <div className="flex items-center gap-2 rounded-full bg-[#f2f3f5] px-3 py-2.5 ring-1 ring-black/5">
                  <Plus className="h-4 w-4 shrink-0 text-black/50" aria-hidden="true"/>
                  <span className="flex-1 truncate text-[13px]">
                    {typedPrompt ? (<span className="text-black/80">
                        {typedPrompt}
                        <span className="ml-px inline-block h-3.5 w-px animate-pulse bg-black/60 align-middle"/>
                      </span>) : (<span className="text-black/40">Ask rentAI</span>)}
                  </span>
                  <Mic className="h-4 w-4 shrink-0 text-black/40" aria-hidden="true"/>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#3D8BFF] text-white">
                    <ArrowUp className="h-4 w-4" aria-hidden="true"/>
                  </span>
                </div>
              </div>
            </div>

            {/* ---- Chat scene ---- */}
            <div className={`absolute inset-0 flex flex-col transition-opacity duration-500 ${scene === "chat" ? "opacity-100" : "pointer-events-none opacity-0"}`}>
              {/* Rental context header with the status badge that flips at the end */}
              <div className="flex items-center gap-2 border-b border-black/5 px-4 py-2.5">
                <img src="/logos/products/rentai-mark.svg" alt="rentAI" className="h-6 w-auto shrink-0 object-contain"/>
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="truncate text-[11px] font-semibold">Rentii</div>
                  <div className="truncate text-[9px] text-black/40">2025 Toyota Camry · #4142-7AV</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[8px] font-semibold uppercase tracking-wide transition-colors duration-500 ${fundsCollected
            ? "bg-emerald-500/15 text-emerald-600 ring-1 ring-emerald-500/30"
            : "bg-[#3D8BFF]/15 text-[#2b62c9]"}`}>
                  {fundsCollected ? "Funds collected" : "On trip"}
                </span>
              </div>

              {/* Message thread */}
              <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-3">
                {/* User prompt bubble */}
                <div className="flex justify-end">
                  <span className="max-w-[75%] rounded-2xl rounded-br-sm bg-[#0b0b12] px-3 py-1.5 text-[12px] leading-snug text-white">
                    {PROMPT}
                  </span>
                </div>

                {/* Thinking */}
                {thinking && (<div className="flex items-center gap-2 text-[11px]">
                    <span className="flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-[#3D8BFF] via-[#7c5cff] to-[#c86bff]">
                      <Sparkles className="h-3 w-3 text-white" aria-hidden="true"/>
                    </span>
                    <span className="rentai-gradient-text font-semibold">Rentii is thinking</span>
                    <span className="flex gap-1">
                      <Dot delay={0}/>
                      <Dot delay={150}/>
                      <Dot delay={300}/>
                    </span>
                  </div>)}

                {/* Violations — laid out in a wide 3-across row to use the landscape width. */}
                {violationsShown > 0 && (<div className="rounded-2xl rounded-bl-sm bg-[#f2f3f5] px-3 py-2.5">
                    <p className="text-[11px] font-medium text-black/70">
                      Found {VIOLATIONS.length} violations on this rental:
                    </p>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {VIOLATIONS.slice(0, violationsShown).map((v) => (<div key={v.title} className="flex flex-col gap-1 rounded-lg bg-white p-2 shadow-sm ring-1 ring-black/5">
                          <div className="flex items-center justify-between gap-1">
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#3D8BFF]/10">
                              <v.icon className="h-3.5 w-3.5 text-[#2b62c9]" aria-hidden="true"/>
                            </span>
                            <span className="text-[12px] font-semibold text-[#0b0b12]">{v.amount}</span>
                          </div>
                          <span className="text-[11px] font-semibold leading-tight">{v.title}</span>
                          <div className="flex flex-col gap-0.5 text-[9px] text-black/45">
                            <span>{v.start}</span>
                            <span>{v.current}</span>
                            <span className="w-fit rounded bg-red-500/10 px-1 font-medium text-red-500">{v.overage}</span>
                          </div>
                        </div>))}
                    </div>
                    {violationsShown >= VIOLATIONS.length && (<div className="mt-2 flex items-center justify-between border-t border-black/10 pt-1.5 text-[11px] font-semibold">
                        <span className="text-black/60">Total overage</span>
                        <span>{TOTAL}</span>
                      </div>)}
                  </div>)}

                {/* Agent question */}
                {askShown && (<div className="flex justify-start">
                    <span className="max-w-[80%] rounded-2xl rounded-bl-sm bg-[#f2f3f5] px-3 py-1.5 text-[12px] leading-snug text-black/80">
                      Do you want me to create an Incident in the Resolution Center and collect the funds?
                    </span>
                  </div>)}

                {/* User reply */}
                {(typedReply || replySent) && (<div className="flex justify-end">
                    <span className="rounded-2xl rounded-br-sm bg-[#0b0b12] px-3 py-1.5 text-[12px] leading-snug text-white">
                      {replySent ? "Yes" : typedReply}
                    </span>
                  </div>)}

                {/* Processing */}
                {processing && (<div className="flex items-center gap-2 text-[11px]">
                    <span className="flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-[#3D8BFF] via-[#7c5cff] to-[#c86bff]">
                      <Sparkles className="h-3 w-3 text-white" aria-hidden="true"/>
                    </span>
                    <span className="rentai-gradient-text font-semibold">Creating incident and collecting funds</span>
                    <span className="flex gap-1">
                      <Dot delay={0}/>
                      <Dot delay={150}/>
                      <Dot delay={300}/>
                    </span>
                  </div>)}

                {/* Success */}
                {fundsCollected && (<div className="flex justify-start">
                    <span className="rentai-success-sheen relative flex max-w-[85%] items-start gap-2 overflow-hidden rounded-2xl rounded-bl-sm bg-emerald-500/10 px-3 py-1.5 text-[12px] leading-snug text-emerald-700 ring-1 ring-emerald-500/30">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true"/>
                      <span className="rentai-emerald-text font-medium">
                        Incident #INC-4821 created and {TOTAL} collected. The booking status is now Funds collected.
                      </span>
                    </span>
                  </div>)}
              </div>

              {/* Input pill (ChatGPT style) — shows the host typing their reply. */}
              <div className="px-4 pb-3">
                <div className="flex items-center gap-2 rounded-full bg-[#f2f3f5] px-3 py-2 ring-1 ring-black/5">
                  <Plus className="h-4 w-4 shrink-0 text-black/50" aria-hidden="true"/>
                  <span className="flex-1 truncate text-[12px]">
                    {typedReply && !replySent ? (<span className="text-black/80">
                        {typedReply}
                        <span className="ml-px inline-block h-3 w-px animate-pulse bg-black/60 align-middle"/>
                      </span>) : (<span className="text-black/40">Reply to Rentii</span>)}
                  </span>
                  <Mic className="h-4 w-4 shrink-0 text-black/40" aria-hidden="true"/>
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#3D8BFF] text-white">
                    <ArrowUp className="h-3.5 w-3.5" aria-hidden="true"/>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes rentaiTextShimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes rentaiSheen {
          0% { transform: translateX(-130%); }
          100% { transform: translateX(230%); }
        }
        .rentai-gradient-text {
          background-image: linear-gradient(90deg,#3D8BFF,#7c5cff,#c86bff,#3D8BFF);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: rentaiTextShimmer 3s linear infinite;
        }
        .rentai-emerald-text {
          background-image: linear-gradient(90deg,#047857,#34d399,#047857);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: rentaiTextShimmer 2.2s linear infinite;
        }
        .rentai-success-sheen::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(100deg, transparent 20%, rgba(255,255,255,0.65) 50%, transparent 80%);
          transform: translateX(-130%);
          animation: rentaiSheen 1.9s ease-in-out 0.15s 2;
        }
        @media (prefers-reduced-motion: reduce) {
          .rentai-gradient-text,
          .rentai-emerald-text { animation: none; }
          .rentai-success-sheen::after { display: none; }
        }
      `}</style>
    </div>);
}
function Dot({ delay }) {
    return (<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-black/40" style={{ animationDelay: `${delay}ms` }} aria-hidden="true"/>);
}
